import React, { useRef, useState } from "react";
import { getChildNameError } from "../../hooks/useClassroomHandoverChildren";
import "./ChildReview.css";

const ATTRIBUTE_COLUMNS = [
  ["eal", "EAL"],
  ["speechAndLanguage", "Speech & Language"],
  ["asd", "ASD"],
  ["gdd", "GDD"],
];

const REMOVAL_REASONS = [
  ["repeating", "Repeating"],
  ["no_longer_in_school", "No longer in school"],
  ["duplicate", "Duplicate"],
  ["other", "Other"],
];
const EMPTY_CHILDREN = [];
const LOCAL_NAME_FEEDBACK = {
  title: "Please resolve this before finishing setup.",
  message: "Duplicate or blank child names must be fixed before continuing.",
};
const API_FINISH_FEEDBACK = {
  conflict: {
    title: "This setup changed elsewhere.",
    message: "The latest class details are shown. Your staged changes have been kept—review them and try again.",
  },
  validation: { title: "We couldn’t finish setup." },
  error: { title: "We couldn’t finish setup." },
};

const getApiFinishErrorMessage = (error) => [
  error?.response?.data?.error?.message,
  error?.response?.data?.message,
  error?.message,
  "Review the highlighted changes and try again.",
].find(Boolean);

const withPendingNames = (children, pendingNames) =>
  children.map((child) => ({ ...child, name: pendingNames[child.id] ?? child.name }));

const getInvalidNameErrors = (children) => children.reduce((errors, child) => {
  if (child.removal) return errors;
  const validation = getChildNameError(children, child.name, child.id);
  return validation ? { ...errors, [child.id]: validation } : errors;
}, {});

const getFinishFeedback = (status, error, hasLocalNameErrors) => {
  const apiFeedback = API_FINISH_FEEDBACK[status];
  if (apiFeedback?.message) return apiFeedback;
  if (apiFeedback) return { ...apiFeedback, message: getApiFinishErrorMessage(error) };
  return hasLocalNameErrors ? LOCAL_NAME_FEEDBACK : null;
};

const focusChildNameInput = (nameInputRefs, childId) => {
  const nameInput = nameInputRefs.current[childId];
  if (!nameInput) return;
  nameInput.focus();
  nameInput.scrollIntoView?.({ block: "center" });
};

const getRemovalDetails = (reason, canonicalStudentId, administrativeNote) => {
  if (reason === "duplicate") return { canonicalStudentId };
  if (reason === "other") return { administrativeNote };
  return {};
};

function RemovalControl({ child, rosterChildren, disabled, onRemove, onUndo }) {
  const [choosing, setChoosing] = useState(false);
  const [reason, setReason] = useState("");
  const [canonicalStudentId, setCanonicalStudentId] = useState("");
  const [administrativeNote, setAdministrativeNote] = useState("");
  const selectableChildren = rosterChildren.filter((option) =>
    option.id !== child.id && !option.isNew && !option.removal
  );
  const duplicateIsInvalid = reason === "duplicate"
    && !selectableChildren.some((option) => option.id === canonicalStudentId.trim());
  const noteLength = administrativeNote.trim().length;
  const noteIsInvalid = reason === "other" && (noteLength < 3 || noteLength > 500);
  if (child.removal) {
    const label = REMOVAL_REASONS.find(([value]) => value === child.removal.reason)?.[1] || "Removed";
    return <div className="handover-removal-state"><span>{label}</span><button type="button" disabled={disabled} onClick={onUndo}>Undo</button></div>;
  }
  if (!choosing) return <button type="button" className="handover-text-action" disabled={disabled} onClick={() => setChoosing(true)}>Remove</button>;
  return (
    <fieldset className="handover-removal-picker">
      <legend>Why remove {child.name}?</legend>
      {REMOVAL_REASONS.map(([value, label]) => (
        <label key={value}><input type="radio" name={`reason-${child.id}`} value={value} disabled={disabled} checked={reason === value} onChange={() => setReason(value)} /> {label}</label>
      ))}
      {reason === "duplicate" && <label>Canonical child
        <select
          value={canonicalStudentId}
          disabled={disabled}
          onChange={(event) => setCanonicalStudentId(event.target.value)}
          aria-label="Canonical child"
        >
          <option value="">Select a child</option>
          {selectableChildren.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
        </select>
      </label>}
      {reason === "other" && <label>Administrative note
        <textarea value={administrativeNote} disabled={disabled} minLength={3} maxLength={500} onChange={(event) => setAdministrativeNote(event.target.value)} aria-label="Administrative note" />
      </label>}
      <div>
        <button
          type="button"
          disabled={disabled || !reason || duplicateIsInvalid || noteIsInvalid}
          onClick={() => {
            onRemove(reason, getRemovalDetails(reason, canonicalStudentId, administrativeNote));
            setChoosing(false);
          }}
        >Mark for removal</button>
        <button type="button" onClick={() => setChoosing(false)}>Cancel</button>
      </div>
    </fieldset>
  );
}

function ChildReview({ state, status, error, onDispatch, onFinish, onBack }) {
  const [newName, setNewName] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [nameErrors, setNameErrors] = useState({});
  const [pendingNames, setPendingNames] = useState({});
  const nameInputRefs = useRef({});
  const isFinishing = status === "finishing";
  const children = state?.children || EMPTY_CHILDREN;
  const projectedChildren = withPendingNames(children, pendingNames);
  const projectedNameErrors = getInvalidNameErrors(projectedChildren);
  const hasLocalNameErrors = Object.keys(projectedNameErrors).some((id) => id !== "add");
  const finishFeedback = getFinishFeedback(status, error, hasLocalNameErrors);
  const hasChanges = children.some((child) => child.isNew || child.removal) || Object.keys(nameErrors).length > 0;
  const dispatchUpdate = (id, field, value) => onDispatch({ type: "update", id, field, value });
  const updateName = (child, value) => {
    const nextPendingNames = { ...pendingNames, [child.id]: value };
    setPendingNames(nextPendingNames);
    setNameErrors((errors) => ({
      ...getInvalidNameErrors(withPendingNames(children, nextPendingNames)),
      ...(errors.add ? { add: errors.add } : {}),
    }));
    dispatchUpdate(child.id, "name", value);
  };

  const addChild = (event) => {
    event.preventDefault();
    const validation = getChildNameError(children, newName);
    if (validation) { setNameErrors((errors) => ({ ...errors, add: validation })); return; }
    onDispatch({ type: "add", name: newName });
    setNewName("");
    setNameErrors((errors) => { const { add, ...rest } = errors; return rest; });
    setAddOpen(false);
  };

  const finish = () => {
    const nextErrors = getInvalidNameErrors(projectedChildren);
    setNameErrors(nextErrors);
    const firstInvalidChildId = projectedChildren.find((child) => nextErrors[child.id])?.id;
    if (firstInvalidChildId) {
      focusChildNameInput(nameInputRefs, firstInvalidChildId);
      return;
    }
    onFinish();
  };

  return (
    <main className="handover-child-review" aria-labelledby="handover-child-review-title">
      <button type="button" className="handover-back-link" disabled={isFinishing} onClick={onBack}>← Back to class setup</button>
      <div className="handover-child-review-heading">
        <div>
          <p className="handover-section-eyebrow">Classroom setup</p>
          <h1 id="handover-child-review-title">Check the children in your class</h1>
          <p>Please check the children in this class and update any information that has changed.</p>
        </div>
        <button type="button" className="handover-add-child" disabled={isFinishing} onClick={() => setAddOpen((open) => !open)}>{addOpen ? "Cancel" : "+ Add New Child"}</button>
      </div>

      {addOpen && <form className="handover-add-child-form" onSubmit={addChild}>
        <label>Full name<input autoFocus value={newName} disabled={isFinishing} onChange={(event) => setNewName(event.target.value)} aria-describedby={nameErrors.add ? "new-child-error" : undefined} placeholder="Enter child’s full name" /></label>
        <p>Enter the name exactly as it appears in your school MIS.</p>
        {nameErrors.add && <p id="new-child-error" className="handover-field-error" role="alert">{nameErrors.add}</p>}
        <button type="submit" disabled={isFinishing}>Add child</button>
      </form>}

      <section className="handover-child-review-list" aria-label="Children in this class">
        <div className="handover-child-table" role="table" aria-label="Children and their support information">
          <div className="handover-child-table-header" role="row">
            <span role="columnheader">Name</span>
            {ATTRIBUTE_COLUMNS.map(([, label]) => <span role="columnheader" key={label}>{label}</span>)}
            <span role="columnheader">Actions</span>
          </div>
          {children.map((child) => {
            const removed = Boolean(child.removal);
            const nameError = nameErrors[child.id] && projectedNameErrors[child.id];
            return <article key={child.id} className={`handover-child-row ${removed ? "is-removed" : ""} ${child.isNew ? "is-new" : ""}`} role="row">
              <div className="handover-child-primary" role="cell">
                <label className="visually-hidden" htmlFor={`child-name-${child.id}`}>Name for {child.name}</label>
                <input
                  ref={(node) => {
                    if (node) {
                      nameInputRefs.current[child.id] = node;
                    } else {
                      delete nameInputRefs.current[child.id];
                    }
                  }}
                  id={`child-name-${child.id}`}
                  value={pendingNames[child.id] ?? child.name}
                  disabled={removed || isFinishing}
                  aria-invalid={Boolean(nameError)}
                  aria-describedby={nameError ? `child-name-${child.id}-error` : undefined}
                  onChange={(event) => updateName(child, event.target.value)}
                  onBlur={(event) => { const value = event.target.value.trim(); setPendingNames((names) => ({ ...names, [child.id]: value })); dispatchUpdate(child.id, "name", value); }}
                />
                {nameError && <span id={`child-name-${child.id}-error`} className="handover-field-error">{nameError}</span>}
                {child.isNew && <span className="handover-new-badge">New</span>}
              </div>
              {ATTRIBUTE_COLUMNS.map(([key, label]) => <div role="cell" className="handover-table-attribute" key={key}><input type="checkbox" aria-label={`${label} for ${child.name}`} checked={Boolean(child[key])} disabled={removed || isFinishing} onChange={(event) => dispatchUpdate(child.id, key, event.target.checked)} /></div>)}
              <div className="handover-child-actions" role="cell"><RemovalControl child={child} rosterChildren={children} disabled={isFinishing} onRemove={(reason, details) => onDispatch({ type: "remove", id: child.id, reason, ...details })} onUndo={() => onDispatch({ type: "undoRemove", id: child.id })} /></div>
            </article>;
          })}
        </div>
      </section>
      {hasChanges && <p className="handover-child-save-note">Your changes will be saved when you finish setup.</p>}
      <footer className="handover-child-review-actions">
        {finishFeedback && <div className="handover-child-review-error" role="alert">
          <strong>{finishFeedback.title}</strong>
          <span>{finishFeedback.message}</span>
        </div>}
        <div className="handover-child-review-action-buttons">
          <button type="button" disabled={isFinishing} onClick={onBack}>← Back</button>
          <button type="button" className="handover-finish-button" disabled={isFinishing} onClick={finish}>{isFinishing ? "Finishing setup…" : "Finish setup →"}</button>
        </div>
      </footer>
    </main>
  );
}

export default ChildReview;
